import React from 'react'
import { Input, FormError, Button, IconPostcard } from '../../../Common'
import { useForm, Controller } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import {
  useSubscribeToNewsletter,
  useLocalLabels,
} from '@sentecacommerce-theme/lib'

import styled from 'styled-components'

import { FooterLabels } from '../../footer.props'

export const NewsletterForm = (props: {
  mobile?: boolean
  labels?: FooterLabels
}) => {
  const labels = useLocalLabels<FooterLabels>()

  const { addToast } = useToasts()
  const { mobile } = props

  const { handleSubmit, errors, control } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  })

  const { subscribe } = useSubscribeToNewsletter({
    callback: () =>
      addToast(labels.newsletterSubscribeSuccess, {
        appearance: 'success',
        autoDismiss: true,
      }),
  })

  const onSubmit = ({ email }: { email: string }) => {
    subscribe(email)
  }

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Title htmlFor={`newsletter-email-${mobile}`}>
        {labels.newsletterEmailLabel}
      </Title>
      <Group>
        <div className="d-flex flex-column flex-md-row align-items-lg-center">
          <Controller
            control={control}
            name="email"
            rules={{
              required: labels.mandatoryField,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: labels.invalidEmail,
              },
            }}
            as={
              <Input
                placeholder={labels.enterEmail}
                id={`newsletter-email-${mobile}`}
                type="email"
              />
            }
          />
          <ErrorMessage>
            <FormError errors={errors} fieldName="email" />
          </ErrorMessage>
          <ButtonContainer>
            <Button type="submit" variant="grey" fullWidth>
              <IconPostcard />
              <NoWrap>{labels.subscribeCallToAction}</NoWrap>
            </Button>
          </ButtonContainer>
        </div>
        <ErrorMessageDesktop>
          <FormError errors={errors} fieldName="email" />
        </ErrorMessageDesktop>
      </Group>
    </Form>
  )
}

const Group = styled.div.attrs({ className: 'pt-1 pt-lg-0' })`
  display: flex;
  flex-direction: column;
  position: relative;

  & input {
    font-size: 1.6rem;
  }

  & button {
    min-width: 18rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    & button {
      border-right: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      min-height: 5.4rem !important;
    }

    & input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      min-height: 5.4rem;
    }

    width: 46rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    width: auto;

    & input {
      width: 28rem;
    }
  }
`

const ErrorMessage = styled.div.attrs({
  className: 'd-lg-none',
})`
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    position: absolute;
    bottom: -2rem;
  }
`

const ErrorMessageDesktop = styled.div.attrs({
  className: 'd-none d-lg-block',
})`
  position: absolute;
  top: 100%;
  margin-top: 0.2rem;
  left: 0.1rem;
`

const ButtonContainer = styled.div.attrs({
  className: 'mt-4 mt-md-0',
})`
  & button {
    display: flex !important;
    align-items: center;
    justify-content: center;

    max-height: 4.4rem;
  }

  & svg {
    width: 2.6rem;
    height: 2.1rem;
    margin-bottom: 0.2rem;
    margin-right: 0.3rem;
  }
`

const Title = styled.label.attrs({
  className: 'mt-1 pb-4 pb-lg-5',
})`
  display: flex;
  align-items: center;
  font-weight: bold;
  line-height: 2.4rem;
  font-size: 2.1rem;
  margin-right: 7.1rem;
`

const Form = styled.form`
  margin-bottom: 1.5rem;

  & input {
    border-color: ${({ theme }) => theme.palette.grey};

    @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
      margin: 0;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    display: flex;
    flex-direction: column;
    margin: auto;
    padding: 1.6rem 0px 3rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl.min}) {
    align-items: baseline;
  }
`

const NoWrap = styled.div`
  white-space: nowrap;
  overflow: hidden;
`
